import {
  createHostOverlayClip,
  coerceHostOverlayTracks,
  hostOverlayClipEndTime,
} from '@/lib/videoEditorHostOverlays';

export { coerceHostOverlayTracks };

export function patchHostOverlayClip(tracks, clipId, patch) {
  return tracks.map((lane) =>
    lane.map((c) => (c.id === clipId ? { ...c, ...patch } : c)),
  );
}

export function removeHostOverlayClip(tracks, clipId) {
  return tracks.map((lane) => lane.filter((c) => c.id !== clipId));
}

export function duplicateHostOverlayClip(tracks, clip) {
  const dur = Math.max(0.1, Number(clip.duration) || 5);
  const end = hostOverlayClipEndTime(clip);
  const copy = {
    ...JSON.parse(JSON.stringify(clip)),
    id: `host-overlay-${Date.now()}`,
    startTime: end + 0.1,
    duration: dur,
    name: `${clip.name || 'Host photo'} (copy)`,
  };
  return tracks.map((lane, idx) => (idx === 0 ? [...lane, copy] : lane));
}

export function addHostOverlayClip(tracks, { playhead = 0, duration = 8, aspectRatio = '16:9', imageUrl = '', name = 'Host photo' } = {}) {
  const clip = createHostOverlayClip({ playhead, duration, aspectRatio, imageUrl, name });
  return tracks.map((lane, idx) => (idx === 0 ? [...lane, clip] : lane));
}

export function beginHostOverlayClipDrag({
  event,
  clip,
  trackIdx,
  pxPerSec,
  overlayTrackRefs,
  setOverlayTracks,
  setSelectedTimelineClip,
  setDraggingClipId,
  dragStateRef,
  clearWindowDragHandlers,
  installWindowDragHandlers,
}) {
  if (event.button !== 0) return;
  if (event.target?.closest?.('[data-host-resize-edge]')) return;
  const sourceTrackNode = overlayTrackRefs.current.get(trackIdx);
  if (!sourceTrackNode) return;

  event.preventDefault();
  setSelectedTimelineClip({ type: 'hostOverlay', id: clip.id });

  const sourceRect = sourceTrackNode.getBoundingClientRect();
  const clipLeftPx = clip.startTime * pxPerSec;
  const dragOffsetPx = Math.max(0, event.clientX - (sourceRect.left + clipLeftPx));

  dragStateRef.current = {
    clipId: clip.id,
    kind: 'hostOverlay',
    dragOffsetPx,
    sourceTrackNode,
  };
  setDraggingClipId(clip.id);

  const handleMove = (moveEvent) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.kind !== 'hostOverlay') return;
    const rect = dragState.sourceTrackNode.getBoundingClientRect();
    const newStart = Math.max(0, (moveEvent.clientX - rect.left - dragState.dragOffsetPx) / pxPerSec);
    setOverlayTracks((prev) =>
      prev.map((lane, idx) =>
        (idx === trackIdx
          ? lane.map((c) =>
            (c.id === clip.id ? { ...c, startTime: newStart } : c),
          )
          : lane),
      ),
    );
  };

  const handleUp = () => {
    dragStateRef.current = null;
    setDraggingClipId(null);
    clearWindowDragHandlers(handleMove, handleUp);
  };

  installWindowDragHandlers(handleMove, handleUp);
}

export function beginHostOverlayClipResize({
  event,
  clip,
  trackIdx,
  edge,
  pxPerSec,
  setOverlayTracks,
  clearWindowDragHandlers,
  installWindowDragHandlers,
}) {
  event.preventDefault();
  event.stopPropagation();
  const startX = event.clientX;
  const origStart = Number(clip.startTime) || 0;
  const origDur = Math.max(0.1, Number(clip.duration) || 5);

  const handleMove = (moveEvent) => {
    const dx = (moveEvent.clientX - startX) / pxPerSec;
    let newStart = origStart;
    let newDur = origDur;
    if (edge === 'left') {
      newStart = Math.max(0, Math.min(origStart + origDur - 0.1, origStart + dx));
      newDur = origStart + origDur - newStart;
    } else {
      newDur = Math.max(0.1, origDur + dx);
    }
    setOverlayTracks((prev) =>
      prev.map((lane, idx) =>
        (idx === trackIdx
          ? lane.map((c) =>
            (c.id === clip.id ? { ...c, startTime: newStart, duration: newDur } : c),
          )
          : lane),
      ),
    );
  };

  const handleUp = () => {
    clearWindowDragHandlers(handleMove, handleUp);
  };

  installWindowDragHandlers(handleMove, handleUp);
}
